/**
 * Listing pagination — append the next page of results
 * (Section Rendering API) instead of replacing the current page.
 */
(() => {
  const TAG = 'product-grid-pagination';

  if (customElements.get(TAG)) return;

  const LIST_SELECTOR = '.product-grid__list, .product-grid__simple';
  const NEXT_SELECTOR = '.product-grid__load-more';

  const parseSectionHtml = (html) => {
    const template = document.createElement('template');

    template.innerHTML = html.trim();

    return template.content;
  };

  const sectionIdFrom = (element) => {
    const fromSelf = element.getAttribute('data-section-id');

    if (fromSelf) return fromSelf;

    const listing = element.closest('[data-product-listing]');
    const fromListing = listing?.getAttribute('data-section-id');

    if (fromListing) return fromListing;

    const wrapper = element.closest('.shopify-section');
    const wrapperId = wrapper?.id || '';

    return wrapperId.replace(/^shopify-section-/, '');
  };

  class ProductGridPagination extends HTMLElement {
    #abort = null;
    #busy = false;

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.#abort?.abort();
    }

    #grid() {
      return this.closest('[data-product-grid]');
    }

    #list() {
      return this.#grid()?.querySelector(LIST_SELECTOR) ?? null;
    }

    #count() {
      return this.querySelector('[data-pagination-count]');
    }

    #actions() {
      return this.querySelector('.product-grid__pagination-actions');
    }

    #onClick = (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest('a.product-grid__load-more');

      if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
      if (!link.href) return;

      event.preventDefault();
      this.#load(link.href);
    };

    async #load(href) {
      if (this.#busy) return;

      const sectionId = sectionIdFrom(this);
      const list = this.#list();

      if (!sectionId || !list) {
        window.location.assign(href);
        return;
      }

      this.#abort?.abort();
      this.#abort = new AbortController();

      const requestUrl = new URL(href, window.location.origin);

      requestUrl.searchParams.delete('sections');
      requestUrl.searchParams.delete('section_id');
      requestUrl.searchParams.set('sections', sectionId);

      this.#setBusy(true);

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: this.#abort.signal,
        });

        if (!response.ok) throw new Error('Failed to load listing page');

        const data = await response.json();
        const html = sectionId ? data[sectionId] : '';

        if (typeof html !== 'string' || html === '') {
          throw new Error('Empty listing section');
        }

        const incoming = parseSectionHtml(html);
        const incomingList = incoming.querySelector(LIST_SELECTOR);
        const incomingPagination = incoming.querySelector(TAG);

        if (!incomingList || incomingList.children.length === 0) {
          throw new Error('Incoming page has no items');
        }

        list.append(...incomingList.children);
        this.#mergeControls(incomingPagination);
      } catch (error) {
        if (error?.name === 'AbortError') return;

        window.location.assign(href);
      } finally {
        this.#setBusy(false);
      }
    }

    #mergeControls(incomingPagination) {
      const currentFrom = Number(this.getAttribute('data-range-from')) || 0;
      const incomingTo = Number(incomingPagination?.getAttribute('data-range-to')) || 0;
      const nextTo = incomingTo || Number(this.getAttribute('data-range-to')) || 0;

      this.setAttribute('data-range-to', String(nextTo));
      this.#updateCount(currentFrom, nextTo);

      const incomingNext = incomingPagination?.querySelector(NEXT_SELECTOR);
      const currentNext = this.querySelector(NEXT_SELECTOR);
      const actions = this.#actions();

      if (incomingNext instanceof HTMLElement) {
        currentNext?.replaceWith(incomingNext);
      } else {
        currentNext?.remove();
        actions?.remove();
      }
    }

    #updateCount(from, to) {
      const count = this.#count();
      const template = this.getAttribute('data-count-template') || '';
      const total = this.getAttribute('data-range-total') || '';

      if (!count || !template) return;

      count.textContent = template
        .replaceAll('__FROM__', String(from))
        .replaceAll('__TO__', String(to))
        .replaceAll('__TOTAL__', String(total));
    }

    #setBusy(busy) {
      this.#busy = busy;
      this.toggleAttribute('aria-busy', busy);

      const link = this.querySelector('a.product-grid__load-more');

      if (!(link instanceof HTMLAnchorElement)) return;

      if (busy) {
        link.setAttribute('aria-disabled', 'true');
      } else {
        link.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define(TAG, ProductGridPagination);
})();
